import { ImageOff } from "lucide-react";

import EmptyState from "@buildpy/ui/empty-state";
import SectionCard from "@buildpy/ui/section-card";
import { isCompletePair, PAIR_STAGE_LABEL, spaceGroupsOf, untaggedCountOf } from "@buildpy/core/modules/portfolio/pairs";
import type { PortfolioImageStage } from "@buildpy/core/modules/portfolio/types";

export interface PairPreviewImage {
  // 보여 줄 수 없는 장(지워짐·잘못된 주소)은 빈 문자열.
  url: string;
  stage?: PortfolioImageStage;
  space?: string;
}

interface Props {
  images: readonly PairPreviewImage[];
  spaces: readonly { code: string; label: string }[];
  // 목록 밖(설정에서 지운) 코드 묶음의 라벨 — 공개는 「기타 공간」, 편집 화면은 「삭제된 공간」.
  unlistedLabel?: string;
}

const STAGES: readonly PortfolioImageStage[] = ["BEFORE", "AFTER"];

const Mini = ({ url, alt }: { url: string; alt: string }) =>
  url ? (
    <img alt={alt} className="size-12 rounded-md object-cover" loading="lazy" src={url} />
  ) : (
    <span className="flex size-12 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <ImageOff className="size-4" />
    </span>
  );

// 편집 화면의 공간별 전·후 미리보기 — 공개 상세와 같은 묶음 함수를 쓴다.
const PortfolioPairPreview = ({ images, spaces, unlistedLabel }: Props) => {
  const groups = spaceGroupsOf(images, spaces, unlistedLabel);
  const untagged = untaggedCountOf(images);
  return (
    <div className="flex flex-col gap-y-2" data-testid="portfolio-pair-preview">
      {groups.length === 0 ? (
        <EmptyState>아직 공사 전·후를 고른 사진이 없습니다.</EmptyState>
      ) : (
        groups.map((group, n) => {
          const complete = isCompletePair(group);
          const missing = group.before.length === 0 ? PAIR_STAGE_LABEL.BEFORE : PAIR_STAGE_LABEL.AFTER;
          return (
            <SectionCard as="div" className="flex flex-col gap-y-1.5" data-testid="portfolio-pair-group" key={`${group.code ?? ""}-${n}`} padding="sm">
              <p className="text-sm font-medium">{group.label}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {STAGES.map((stage) => {
                  const list = stage === "BEFORE" ? group.before : group.after;
                  return (
                    <div className="flex flex-col gap-y-1" key={stage}>
                      <p className="text-xs text-muted-foreground">
                        {PAIR_STAGE_LABEL[stage]} {list.length}장
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {list.map((image, i) => (
                          <Mini alt={`${group.label} ${PAIR_STAGE_LABEL[stage]}`} key={`${stage}-${i}`} url={image.url} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {!complete && (
                <p className="text-xs text-muted-foreground" data-testid="portfolio-pair-incomplete">
                  {missing} 사진이 없어 공개 페이지의 공간별 전·후에는 나가지 않습니다.
                </p>
              )}
            </SectionCard>
          );
        })
      )}
      {untagged > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="portfolio-pair-untagged">
          전·후를 고르지 않은 사진 {untagged}장은 공간별 묶음에 들어가지 않습니다.
        </p>
      )}
    </div>
  );
};

export default PortfolioPairPreview;
